import { type BadgeColor } from '@precis-digital/kurama/Badge'
import { type TFunction } from 'shared/translations'
import { type ConfigStatus, STATUS_COLOR_MAP, STATUSES } from 'shared/configStatuses'
import { type CirrusCollection, type CirrusSourceOption } from 'shared/copilot/cirrusSource'
import { type FormatLastUpdateFn, type StatusLabelFn } from './buildCopilotSourceMenuOptions'

const isHealthyCirrusStatus = (status: ConfigStatus): boolean =>
  status === STATUSES.active || status === STATUSES.connected

const getCirrusCollectionTypeLabel = (collection: CirrusCollection, t: TFunction<'common', 'copilot'>): string => {
  switch (collection) {
    case 'event_feed':
      return t('sourceMenu.recommendations')
    case 'standard_reports':
      return t('sourceMenu.reportingSolutions')
    case 'attribution':
      return t('sourceMenu.attributionModels')
    case 'bo':
      return t('sourceMenu.budgetOptimisers')
    case 'enhanced_signals':
      return t('sourceMenu.biddingSignals')
  }
}

export interface CirrusChipTooltipModel {
  collection: CirrusCollection
  headerTitle: string
  headerSubtitle?: string
  statusText: string
  statusColor: BadgeColor
  detailCaptions: string[]
  showStatusBody: boolean
  status?: ConfigStatus
  statusDetail?: CirrusSourceOption['statusDetail']
}

/**
 * Pure branching for the selected-Cirrus chip tooltip (healthy vs pipeline status,
 * template caption, paused last-update fallback, StatusTooltipBody visibility).
 */
export const getCirrusChipTooltipModel = ({
  option,
  t,
  tCommon,
  formatLastUpdate,
  getStatusLabel
}: {
  option: CirrusSourceOption
  t: TFunction<'common', 'copilot'>
  tCommon: TFunction
  formatLastUpdate: FormatLastUpdateFn
  getStatusLabel: StatusLabelFn
}): CirrusChipTooltipModel => {
  const collectionTypeLabel = getCirrusCollectionTypeLabel(option.collection, t)
  // Recommendations is a single leaf whose label is already the product name — repeating it
  // as the subtitle would be redundant.
  const headerSubtitle = option.label === collectionTypeLabel ? undefined : collectionTypeLabel

  const status = option.status
  const statusDetail = option.statusDetail
  const statusColor: BadgeColor = status != null ? STATUS_COLOR_MAP[status] ?? 'neutral' : 'semantic-success'
  // Healthy / status-less chips keep the selection copy; otherwise surface the pipeline word
  // (Paused, Error, Running…) so the chip tooltip matches what the picker shows.
  const statusText =
    status == null || isHealthyCirrusStatus(status)
      ? t('dataAgent.sourceTooltip.currentlySelected')
      : getStatusLabel(status)

  const detailCaptions: string[] = []
  if (option.annotation != null && option.annotation !== '') {
    detailCaptions.push(t('dataAgent.sourceTooltip.templateId', { templateId: option.annotation }))
  }
  if (statusDetail != null) {
    // Paused configs often have no last-run timestamp; "Pending" reads as if a run is
    // queued, so prefer the paused label when there is nothing to date.
    const lastUpdateValue =
      status === STATUSES.paused && statusDetail.lastUpdated == null
        ? getStatusLabel(STATUSES.paused)
        : formatLastUpdate(statusDetail.lastUpdated)
    detailCaptions.push(`${tCommon('lastUpdate')}: ${lastUpdateValue}`)
  }

  const showStatusBody = status != null && statusDetail != null && !isHealthyCirrusStatus(status)

  return {
    collection: option.collection,
    headerTitle: option.label,
    headerSubtitle,
    statusText,
    statusColor,
    detailCaptions,
    showStatusBody,
    status,
    statusDetail
  }
}
